import React from "react";
import Skeleton from "../ui/Skeleton";

export const AdsManagerSkeleton: React.FC = () => {
  return (
    <div className="w-full space-y-3 animate-pulse">
      {/* Stepper Skeleton matching actual index.tsx (4 steps by default;
          a 5th "Leads" step is only added conditionally at runtime) */}
      <div className="bg-white border border-purple-200/70 rounded-2xl p-2.5 px-3 sm:px-4 shadow-xs mb-3">
        <div className="flex items-center justify-between gap-2">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className="flex items-center gap-2 px-2.5 py-1 rounded-xl flex-1 max-w-[200px]"
            >
              <Skeleton
                variant="rectangular"
                width={32}
                height={32}
                className="rounded-xl shrink-0"
              />
              <div className="space-y-1 flex-1 min-w-0 hidden sm:block">
                <Skeleton
                  variant="text"
                  width="70%"
                  height="0.65rem"
                  className="rounded-sm"
                />
                <Skeleton
                  variant="text"
                  width="90%"
                  height="0.5rem"
                  className="rounded-sm opacity-50 hidden lg:block"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area: Step 1 Simulation */}
      <div className="space-y-4 mb-4 pb-10">
        {/* Platform Selector Card Skeleton */}
        <div className="bg-white rounded-2xl p-4 md:p-5 border border-purple-200/70 shadow-xs space-y-3">
          <div className="flex items-center justify-between border-b pb-2.5 border-purple-100">
            <div className="flex items-center gap-2.5">
              <Skeleton
                variant="rectangular"
                width={32}
                height={32}
                className="rounded-xl"
              />
              <div className="space-y-1">
                <Skeleton
                  variant="text"
                  width={140}
                  height="0.85rem"
                  className="rounded-sm"
                />
                <Skeleton
                  variant="text"
                  width={200}
                  height="0.6rem"
                  className="rounded-sm opacity-50"
                />
              </div>
            </div>
            <Skeleton
              variant="rectangular"
              width={80}
              height={20}
              className="rounded-full"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50"
              >
                <Skeleton
                  variant="rectangular"
                  width={40}
                  height={40}
                  className="rounded-xl shrink-0"
                />
                <div className="space-y-1 flex-1 min-w-0">
                  <Skeleton
                    variant="text"
                    width="60%"
                    height="0.75rem"
                    className="rounded-sm"
                  />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height="0.55rem"
                    className="rounded-sm opacity-50"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tactical Config Card Skeleton */}
        <div className="bg-white rounded-2xl p-4 md:p-5 border border-purple-200/70 shadow-xs space-y-4">
          <div className="flex items-center gap-2.5 border-b pb-3 border-purple-100">
            <Skeleton
              variant="rectangular"
              width={32}
              height={32}
              className="rounded-xl"
            />
            <div className="space-y-1">
              <Skeleton
                variant="text"
                width={180}
                height="0.85rem"
                className="rounded-sm"
              />
              <Skeleton
                variant="text"
                width={220}
                height="0.6rem"
                className="rounded-sm opacity-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Skeleton
                variant="text"
                width={100}
                height="1.25rem"
                className="rounded-sm"
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Skeleton
                variant="text"
                width={120}
                height="1.25rem"
                className="rounded-sm"
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Skeleton
                variant="text"
                width={110}
                height="1.25rem"
                className="rounded-sm"
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Skeleton
                variant="text"
                width={130}
                height="1.25rem"
                className="rounded-sm"
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                className="rounded-xl"
              />
            </div>
          </div>

          {/* Objetivo de Desempenho block (mirrors Step1_GeneralConfig's
              performance-goal grid, present at default meta+website state) */}
          <div className="pt-3.5 border-t border-purple-100/70 space-y-2">
            <Skeleton
              variant="text"
              width={160}
              height="0.7rem"
              className="rounded-sm"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {[1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  width="100%"
                  height={42}
                  className="rounded-xl"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Action Bar Skeleton */}
      <div className="sticky bottom-4 py-2.5 px-5 bg-white/95 backdrop-blur-xl border border-purple-200/70 rounded-2xl flex items-center justify-between z-40 shadow-lg shadow-purple-900/10">
        <Skeleton
          variant="rectangular"
          width={80}
          height={36}
          className="rounded-xl"
        />
        <div className="flex items-center gap-3">
          <Skeleton
            variant="rectangular"
            width={110}
            height={36}
            className="rounded-xl"
          />
          <Skeleton
            variant="rectangular"
            width={130}
            height={36}
            className="rounded-xl"
          />
        </div>
      </div>
    </div>
  );
};

export default AdsManagerSkeleton;
